import React from 'react';
import { cn } from '../../../lib/cn';

/**
 * Card and CardHeader, the surface everything else sits on.
 *
 * Source selectors: `system.css` `.dsk-card`, `.dsk-card--flush`,
 * `.dsk-card__header`.
 *
 * Doctrine enforced:
 * - BORDERLESS, tone-lift depth. A card is `--bg-card` on a `--dsk-ground`
 *   page, with no border and no shadow. Shadows belong to floating layers only:
 *   popover, modal, dropdown, drawer, toast.
 * - Which is exactly why a page background must be `var(--dsk-ground)` and
 *   never `--bg-secondary`: in dark those two collapse onto `--bg-card` and the
 *   card disappears into the page.
 * - `flush` drops the padding and clips the corners, for a card whose body is a
 *   table or a list of rows that brings its own insets.
 */
export const Card = React.forwardRef(function Card(
  { flush = false, as: Component = 'div', className, ...rest },
  ref,
) {
  return (
    <Component
      ref={ref}
      className={cn('dsk-card', flush && 'dsk-card--flush', className)}
      {...rest}
    />
  );
});
Card.displayName = 'Card';

/**
 * CardHeader, `.dsk-card__header`. Title on the left, at most one action on the
 * right. Two buttons in a section header and the section becomes a toolbar.
 */
export const CardHeader = React.forwardRef(function CardHeader({ className, ...rest }, ref) {
  return <div ref={ref} className={cn('dsk-card__header', className)} {...rest} />;
});
CardHeader.displayName = 'CardHeader';

export default Card;
